import { DeviceOperationalState } from '@/__generated__/graphql';
import { useModalStatusMessage } from '@/hooks';
import { useMutation } from '@apollo/client';
import { t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { useMemo } from 'react';
import { UPDATE_OPERATIONAL_STATE } from '../graphql/mutations/updateOperationalState';
import { useUpdateOperationalStateCompleteCallback } from './useOnCompleteUpdateOperationalState';
import { useOperationalState } from './useOperationalState';

type Options = {
  onCompleted: () => void;
};

export const useUpdateOperationalStateMutation = (deviceId: string, options?: Options) => {
  const { i18n } = useLingui();
  const { openModalErrorMessage, openModalSuccessMessage } = useModalStatusMessage();
  const { operationalState } = useOperationalState(deviceId);
  const defaultOnCompleted = useUpdateOperationalStateCompleteCallback(deviceId);

  const mutation = useMutation(UPDATE_OPERATIONAL_STATE);

  return useMemo(() => {
    const [update, { loading }] = mutation;
    const prevOperationalState = operationalState;

    return {
      currentState: operationalState,
      loading,
      updateOperationalState: async (operationalState: DeviceOperationalState) => {
        return await update({
          variables: {
            deviceId,
            operationalState,
          },
          onCompleted(data) {
            const newOperationalState = data.updateDevice.profile.operationalState;
            openModalSuccessMessage(t(i18n)`Dispositivo actualizado correctamente`);
            defaultOnCompleted(prevOperationalState, newOperationalState);
            if (options?.onCompleted) {
              options.onCompleted();
            }
          },
          onError(error) {
            openModalErrorMessage(t(i18n)`Error al actualizar el dispositivo: ${error.message}`);
          },
          update(cache) {
            cache.modify({
              id: cache.identify({ __typename: 'DeviceProfile', id: deviceId }),
              fields: {
                operationalState(_, { DELETE }) {
                  return DELETE;
                },
                operationalStates(_, { DELETE }) {
                  return DELETE;
                },
              },
            });
            cache.modify({
              fields: {
                devicesConnection(_, { DELETE }) {
                  return DELETE;
                },
                devices(_, { DELETE }) {
                  return DELETE;
                },
              },
            });
          },
          async onQueryUpdated(observableQuery) {
            return await observableQuery.refetch();
          },
        });
      },
    };
  }, [
    mutation,
    operationalState,
    i18n,
    openModalErrorMessage,
    openModalSuccessMessage,
    deviceId,
    defaultOnCompleted,
    options,
  ]);
};
